import * as React from "react";
import { styled, alpha, useTheme } from "@mui/material/styles";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import SideBar from "./SideBar";
import InputBase from "@mui/material/InputBase";
import MenuIcon from "@mui/icons-material/Menu";
import { makeStyles } from "@mui/styles";
import clsx from "clsx";
import { Avatar, Badge, Button, MenuItem } from "@mui/material";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { COUNT_UNREAD_NOTIFICATIONS, CREATE_POST } from "@constants/api";
import axios from "@MyAxios";
import logo from "@images/logo.png";
import { useDispatch, useSelector } from "react-redux";
import { resetPosts, setClickedPostId } from "@redux/prevPostsSlice";
import CustomSelect from "./CustomSelect";
import { Add as AddIcon, FormatListBulleted, GridView } from "@mui/icons-material";
import HomepageFilter from "./HomepageFilter";
import { clearGroups, setIsGroupOnlyPost } from "@redux/postSlice";
import HomePageCompactViewToggle from "./HomepageCompactViewToggle";

const useStyles = makeStyles((theme) => ({
  border: {
    borderWidth: 1,
    borderColor: "#CACACA",
  },
  textColor: {
    color: theme.palette.primary.main,
  },
}));
const Search = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: "20px",
  border: "1px solid #CACACA",
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  "&:hover": {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginLeft: 0,
  width: "100%",
  [theme.breakpoints.up("sm")]: {
    marginLeft: theme.spacing(1),
    width: "auto",
  },
  [theme.breakpoints.up("md")]: {
    width: "260px",
  },
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  "& .MuiInputBase-input": {
    paddingLeft: "10px",
  },
  cursor: "pointer",
}));

export default function Header({
  filters = false,
  handleGroupChange,
  handleCommunityChange,
  isCompactView,
  setIsCompactView,
}) {
  const theme = useTheme();
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();


  const { userRecord } = useSelector((state) => state.user);
  const isAnonymousUser = !Boolean(userRecord?.id);
  const { posts } = useSelector(state => state.prevPosts);
  const { isGroupOnlyPost, selectedGroups } = useSelector((state) => state.post);
  const locationHook = useLocation();

  const [open, setOpen] = React.useState(false);
  const [location, setLocation] = React.useState(window.location.pathname);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = React.useState(0);

  const handleOpen = () => {
    if (isAnonymousUser) return;
    setOpen(true);
  };
  const handleClose = () => setOpen(false);

  const handleLogoClick = () => {
    if (window.location.pathname !== '/') {
      dispatch(resetPosts());
      dispatch(setClickedPostId());
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  React.useEffect(() => {
    if (isAnonymousUser) return;

    axios
      .get(COUNT_UNREAD_NOTIFICATIONS)
      .then((res) => {
        setUnreadNotificationsCount(res.data);
      })
      .catch(() => { });
  }, []);

  const postingIsDisabled = isAnonymousUser || !Boolean(userRecord?.joinedCommunities?.some(com => com.disabledAt === 0));

  return (
    <div>
      <div className={clsx("fixed z-50 w-full bg-white md:w-1/2", filters ? 'h-28' : 'h-14')}>
        <Toolbar className="flex items-center justify-between">
          <div className="flex items-center">
            <Link
              to="/"
              onClick={handleLogoClick}
            >
              <img src={logo} alt="Logo" className="mr-3 w-28" />
            </Link>
            <Link
              to={"/search"}
              state={{ prevLocation: location }}
              className=" cursor-pointer"
              onClick={(e) => {
                if (isAnonymousUser) e.preventDefault();
              }}
            >
              <Search>
                <StyledInputBase
                  placeholder="Search"
                  inputProps={{ "aria-label": "search" }}
                  disabled={isAnonymousUser}
                />
              </Search>
            </Link>
            <IconButton
              onClick={() => {
                if (isAnonymousUser) return;

                return navigate("/notifications", {
                  state: {
                    from: window.location.pathname,
                    postState: {
                      selectedGroups,
                      isGroupOnlyPost,
                    },
                    originLocationState: locationHook.state,
                  },
                });
              }
              }
            >
              {unreadNotificationsCount > 0 ? (
                <Badge
                  color="secondary"
                  badgeContent={unreadNotificationsCount}
                >
                  <NotificationsNoneOutlinedIcon
                    sx={{ color: "#b4b4b4", width: "40px", height: "30px" }}
                  />
                </Badge>
              ) : (
                <NotificationsNoneOutlinedIcon
                  sx={{ color: "#b4b4b4", width: "40px", height: "30px" }}
                />
              )}
            </IconButton>
          </div>

          <IconButton
            onClick={handleOpen}
            edge="start"
            aria-label="open drawer"
            sx={{ color: theme.palette.primary.main }}
          >
            <MenuIcon fontSize="large" />
          </IconButton>
        </Toolbar>

        {window.location.pathname === '/' && (
          <div className="flex justify-between items-center px-4">
            {(!postingIsDisabled) ? (
              <Button
                variant="outlined"
                size="small"
                className="-case text-lg"
                startIcon={<AddIcon />}
                onClick={() => {
                  if (isGroupOnlyPost) {
                    dispatch(clearGroups());
                    dispatch(setIsGroupOnlyPost(false));
                  }
                  navigate(CREATE_POST);
                }}
              >
                <Typography className="normal-case">
                  Post Stream
                </Typography>
              </Button>
            ) : (
              <div />
            )}

            {!isAnonymousUser && <div className="ml-auto mr-5">
              <HomePageCompactViewToggle isCompactView={isCompactView} setIsCompactView={setIsCompactView} />
            </div>}

            <HomepageFilter
              handleGroupChange={handleGroupChange}
              handleCommunityChange={handleCommunityChange}
            />
          </div>
        )}

        <SideBar open={open} handleClose={handleClose} />
      </div>
    </div>
  );
}
